import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Reveal, Lines, EASE } from "../components/site/Motion";

const EMPTY = { name: "", email: "", phone: "", cap: "", city: "", message: "", privacy: false };

export default function TestDrivePage() {
  const [params] = useSearchParams();
  const qParam = params.get("q") || "";
  const centroParam = params.get("centro") || "";
  const [centres, setCentres] = useState([]);
  const [centreId, setCentreId] = useState(centroParam);
  const [form, setForm] = useState({ ...EMPTY, cap: qParam });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Prenota un test drive — SWAPA";
  }, []);

  useEffect(() => {
    const p = new URLSearchParams({ role: "test_drive" });
    if (qParam) p.set("q", qParam);
    api.get(`/centres?${p}`).then((r) => setCentres(r.data)).catch(() => {});
  }, [qParam]);

  const selected = useMemo(() => centres.find((c) => c.id === centreId), [centres, centreId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.privacy) {
      setError("Il consenso al trattamento dei dati è obbligatorio per essere ricontattato.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads/test-drive", {
        name: form.name, email: form.email, phone: form.phone, cap: form.cap,
        city: form.city, centre_id: centreId || null, message: form.message, privacy: form.privacy,
      });
      setDone(true);
      toast.success("Richiesta inviata. Il centro SWAPA ti ricontatterà.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-white/15 bg-ink px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 focus:border-swapa-yellow focus:outline-none";

  return (
    <div data-testid="testdrive-page" className="bg-ink pb-28 pt-32 sm:pt-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-swapa-yellow">Test drive</p>
        <h1 className="mt-6 font-display font-black uppercase leading-[0.9] tracking-tighter">
          <Lines animate="load" lines={["Don't just look.", "Drive it."]} className="text-4xl text-white sm:text-6xl lg:text-7xl" />
        </h1>

        <div className="mt-16 grid gap-14 lg:grid-cols-2">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Centri test drive {qParam && `— ricerca: "${qParam}"`}</p>
            <ul className="mt-6 space-y-3" data-testid="testdrive-centre-list">
              {centres.map((c) => (
                <li key={c.id}>
                  <button
                    data-testid={`testdrive-centre-${c.id}`}
                    onClick={() => setCentreId(c.id)}
                    className={`w-full border p-5 text-left transition-colors duration-300 ${
                      centreId === c.id ? "border-swapa-yellow bg-white/5" : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <p className="flex items-center gap-2 font-display text-lg font-bold uppercase tracking-tight text-white">
                      <MapPin className="h-4 w-4 text-swapa-yellow" /> {c.name}
                    </p>
                    {c.host && (
                      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400">presso {c.host}</p>
                    )}
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                      {c.address} — {c.cap} {c.city} ({c.province}) {c.hours && `· ${c.hours}`}
                    </p>
                  </button>
                </li>
              ))}
              {centres.length === 0 && (
                <li data-testid="testdrive-no-results" className="border border-white/10 p-6 text-sm text-zinc-500">
                  Nessun centro test drive trovato per "{qParam}". Lascia i tuoi dati: ti ricontatteremo
                  per organizzare una prova nella tua zona.
                </li>
              )}
            </ul>
          </div>

          <div>
            {done ? (
              <motion.div
                data-testid="testdrive-success"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="border border-swapa-yellow/40 bg-card p-10"
              >
                <span className="flex h-12 w-12 items-center justify-center bg-swapa-yellow"><Check className="h-6 w-6 text-black" /></span>
                <h2 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight text-white">Ci vediamo in strada.</h2>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">
                  La tua richiesta è stata registrata. Il centro SWAPA {selected ? `di ${selected.city} ` : ""}
                  ti ricontatterà per fissare data e ora del tuo test drive.
                </p>
              </motion.div>
            ) : (
              <Reveal>
                <form onSubmit={submit} data-testid="testdrive-form" className="border border-white/10 bg-card p-6 sm:p-10">
                  <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">Prenota la tua prova</h2>
                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    <input data-testid="testdrive-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nome e cognome *" className={inputCls} />
                    <input data-testid="testdrive-email-input" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email *" className={inputCls} />
                    <input data-testid="testdrive-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Telefono" className={inputCls} />
                    <input data-testid="testdrive-cap-field" value={form.cap} onChange={(e) => set("cap", e.target.value)} placeholder="CAP" className={inputCls} />
                    <input data-testid="testdrive-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Città" className={`${inputCls} sm:col-span-2`} />
                    <textarea data-testid="testdrive-message-input" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Note o preferenze di orario" rows={3} className={`${inputCls} sm:col-span-2`} />
                  </div>
                  <label className="mt-6 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-zinc-400">
                    <input
                      data-testid="testdrive-privacy-checkbox"
                      type="checkbox"
                      checked={form.privacy}
                      onChange={(e) => set("privacy", e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#FFD700]"
                    />
                    Ho letto l'informativa privacy e acconsento al trattamento dei miei dati per essere ricontattato in merito al test drive. *
                  </label>
                  {error && <p data-testid="testdrive-error" className="mt-4 text-sm text-red-400">{error}</p>}
                  <button
                    type="submit"
                    data-testid="testdrive-submit-button"
                    disabled={loading}
                    className="mt-8 w-full bg-swapa-yellow py-4 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-black transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    {loading ? "Invio in corso…" : "Invia richiesta test drive"}
                  </button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
